import type { MentionKind } from "@repo/shared/types/mention-part";

import type { Block } from "~/stores/blocks/blocks.types";
import type { Board } from "~/stores/boards/boards.types";
import type { Dataset } from "~/stores/datasets/datasets.types";
import type { Datasource } from "~/stores/datasources/datasources.types";

/** Cap per group so the popover stays scannable. */
const PER_GROUP_LIMIT = 8;

export type MentionItem = {
  /** Stable composite key for React (`${kind}:${id}`). */
  key: string;
  kind: MentionKind;
  id: string;
  /** Primary display text (entity name). */
  label: string;
};

/**
 * Case-insensitive substring score. Returns -1 if `query` does not match.
 *
 * Lower is better (0 = exact prefix). We avoid regex on raw input so the
 * helper never throws on `(`, `/`, `.`, etc.
 */
const score = (haystack: string, query: string): number => {
  if (!query) return 0;
  const h = haystack.toLowerCase();
  const q = query.toLowerCase();
  const idx = h.indexOf(q);
  if (idx === -1) return -1;
  return idx;
};

const matchScore = (
  query: string,
  ...fields: Array<string | null | undefined>
): number => {
  // No query: every item matches with neutral score. Without this an entity
  // whose searchable fields are all empty (e.g. an "Untitled block") would
  // be skipped because the loop below `continue`s past empty fields and
  // `best` would stay -1.
  if (!query) return 0;
  let best = -1;
  for (const f of fields) {
    if (!f) continue;
    const s = score(f, query);
    if (s === -1) continue;
    if (best === -1 || s < best) best = s;
  }
  return best;
};

const buildBlockItem = (block: Block): MentionItem => ({
  key: `block:${block.id}`,
  kind: "block",
  id: block.id,
  label: block.name?.trim() || "Untitled block",
});

const buildBoardItem = (board: Board): MentionItem => ({
  key: `board:${board.id}`,
  kind: "board",
  id: board.id,
  label: board.name?.trim() || "Untitled board",
});

const buildDatasetItem = (dataset: Dataset): MentionItem => ({
  key: `dataset:${dataset.id}`,
  kind: "dataset",
  id: dataset.id,
  label: dataset.name?.trim() || "Untitled dataset",
});

const buildDatasourceItem = (datasource: Datasource): MentionItem => ({
  key: `datasource:${datasource.id}`,
  kind: "datasource",
  id: datasource.id,
  label: datasource.name?.trim() || "Untitled datasource",
});

/**
 * Filters and ranks the three entity stores by `query`, returning a flat list
 * grouped by kind. Each group is independently capped at {@link PER_GROUP_LIMIT}
 * to keep the popover dense and predictable.
 *
 * Pure: callers pass in already-snapshotted entity arrays from Legend State.
 */
export const searchMentions = ({
  query,
  blocks,
  boards,
  datasets,
  datasources,
}: {
  query: string;
  blocks: Block[];
  boards: Board[];
  datasets: Dataset[];
  datasources: Datasource[];
}): { kind: MentionKind; items: MentionItem[] }[] => {
  const trimmed = query.trim();

  const rankedBlocks = blocks
    .map((b) => ({
      item: buildBlockItem(b),
      s: matchScore(trimmed, b.name, b.description),
    }))
    .filter((r) => r.s !== -1)
    .sort((a, b) => a.s - b.s)
    .slice(0, PER_GROUP_LIMIT)
    .map((r) => r.item);

  const rankedBoards = boards
    .map((b) => ({
      item: buildBoardItem(b),
      s: matchScore(trimmed, b.name, b.description),
    }))
    .filter((r) => r.s !== -1)
    .sort((a, b) => a.s - b.s)
    .slice(0, PER_GROUP_LIMIT)
    .map((r) => r.item);

  const rankedDatasets = datasets
    .map((d) => ({
      item: buildDatasetItem(d),
      s: matchScore(trimmed, d.name),
    }))
    .filter((r) => r.s !== -1)
    .sort((a, b) => a.s - b.s)
    .slice(0, PER_GROUP_LIMIT)
    .map((r) => r.item);

  const rankedDatasources = datasources
    .map((d) => ({
      item: buildDatasourceItem(d),
      s: matchScore(trimmed, d.name, d.description, d.type),
    }))
    .filter((r) => r.s !== -1)
    .sort((a, b) => a.s - b.s)
    .slice(0, PER_GROUP_LIMIT)
    .map((r) => r.item);

  return [
    { kind: "block" as const, items: rankedBlocks },
    { kind: "board" as const, items: rankedBoards },
    { kind: "dataset" as const, items: rankedDatasets },
    { kind: "datasource" as const, items: rankedDatasources },
  ];
};

/** Flattens grouped results to a single ordered list (for keyboard nav). */
export const flattenMentionItems = (
  groups: { kind: MentionKind; items: MentionItem[] }[],
): MentionItem[] => groups.flatMap((g) => g.items);

export const MENTION_KIND_LABEL: Record<MentionKind, string> = {
  block: "Blocks",
  board: "Boards",
  dataset: "Datasets",
  datasource: "Datasources",
};
